import { Box } from '@wix/design-system';
import { DomainSearchSuiteModule, Statuses } from '@wix/domains-search-suite';
import { HarmonyThemeProvider } from '@wix/core-domain-search';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { FAQs } from '../../Components/FAQs';
import { GateTldChips } from '../../Components/GateTldChips';
import { AccountGateHeader } from '../../Components/Headers/AccountGateHeader';
import { MoreTools } from '../../Components/MoreTools';
import { SocialProofCard } from '../../Components/SocialProofCard';
import { TldPromotionBoxes } from '../../Components/TldPromotionBoxes';
import { useDomainSearchGateBi } from '../../hooks/useDomainSearchGateBi';
import { useGatePageBackground } from '../../hooks/useGatePageBackground';
import { useGateScrollBi } from '../../hooks/useGateScrollBi';
import { useQuerySubmit } from '../../hooks/useQuerySubmit';
import { prefetchRecommendations } from '../../hooks/useRecommendations';
import { useSuggestedTldPrices } from '../../hooks/useSuggestedTldPrices';
import { useViewTransition } from '../../hooks/useViewTransition';
import { ViewMode } from '../../types';
import { useIsBmHarmonyThemeEnabled } from '../../utils/harmonyTheme';
import { HeroSearchBar } from '../SiteLevelGateOptimized/HeroSearchBar';
import s from '../SiteLevelGateOptimized/siteLevelGateHomeOptimized.scss';
import transition from '../viewTransitions.css';
import { AccountGateVoucherBanner } from './AccountGateVoucherBanner';
const AccountLevelGateContent = _ref => {
  let hideBackButton = _ref.hideBackButton;
  useSuggestedTldPrices();
  const _useViewTransition = useViewTransition(),
    navigateBack = _useViewTransition.navigateBack,
    navigateTo = _useViewTransition.navigateTo;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.SDK.hooks.useUrlRecovery(),
    getUrlState = _DomainSearchSuiteMod.getUrlState;
  const pageBackground = useGatePageBackground();
  const pageRef = useRef(null);
  const searchbarRef = useRef(null);
  const tldGridRef = useRef(null);
  const faqRef = useRef(null);
  useDomainSearchGateBi({
    viewMode: ViewMode.ACCOUNT_LEVEL_GATE,
    pageType: 'landing'
  });
  useGateScrollBi({
    containerRef: pageRef,
    sections: [{
      section: 'searchbar',
      ref: searchbarRef
    }, {
      section: 'tld_grid',
      ref: tldGridRef
    }, {
      section: 'faq',
      ref: faqRef
    }]
  });
  useQuerySubmit({});
  return /*#__PURE__*/React.createElement(Box, {
    ref: pageRef,
    direction: "vertical",
    align: "center",
    width: "100%",
    height: "100%",
    overflow: "auto",
    backgroundColor: pageBackground,
    scrollBehavior: "smooth",
    boxSizing: "border-box",
    padding: "24px 40px"
  }, /*#__PURE__*/React.createElement("section", {
    className: clsx(s.firstFold, s.firstFoldAutoHeight),
    ref: searchbarRef
  }, /*#__PURE__*/React.createElement("div", {
    className: s.voucherBanner
  }, /*#__PURE__*/React.createElement(AccountGateVoucherBanner, null)), /*#__PURE__*/React.createElement(Box, {
    className: `${s.hero} ${transition.header}`,
    direction: "vertical",
    align: "center",
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "8px",
    backgroundColor: "#E6E2FF"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    align: "center",
    gap: "20px",
    width: "100%"
  }, /*#__PURE__*/React.createElement(AccountGateHeader, {
    className: s.heroHeader,
    gap: "20px",
    titleKey: "domains_search_gate.account_level.gate.title",
    onBackClick: hideBackButton ? undefined : () => {
      DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
        ctaClick: 'back_button'
      });
      navigateBack();
    }
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "12px",
    maxWidth: "800px",
    className: `${s.searchBarWrapper} ${transition.searchBar}`
  }, /*#__PURE__*/React.createElement(HeroSearchBar, {
    compact: true,
    onSubmit: filters => {
      DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
        ctaClick: 'search',
        value: filters.query || undefined
      });
      prefetchRecommendations();
      setTimeout(() => {
        const searchParams = getUrlState(filters);
        navigateTo(`/results?${searchParams}`);
      }, 500);
    }
  }), /*#__PURE__*/React.createElement(GateTldChips, {
    voucherScope: "account"
  }))))), /*#__PURE__*/React.createElement("main", {
    className: `${s.mainContent} ${transition.content}`,
    tabIndex: -1
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    className: s.content
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    marginBlock: "16px"
  }, /*#__PURE__*/React.createElement(SocialProofCard, null)), /*#__PURE__*/React.createElement("div", {
    ref: tldGridRef
  }, /*#__PURE__*/React.createElement(TldPromotionBoxes, {
    voucherAwarePricing: true,
    tldPressFillsSearch: true,
    voucherScope: "account"
  })), /*#__PURE__*/React.createElement(MoreTools, null), /*#__PURE__*/React.createElement("div", {
    ref: faqRef
  }, /*#__PURE__*/React.createElement(FAQs, {
    onToggle: index => {
      DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
        ctaClick: 'asked_questions',
        value: JSON.stringify(index)
      });
    }
  })))));
};
export const AccountLevelGate = _ref2 => {
  let handlers = _ref2.handlers,
    hideBackButton = _ref2.hideBackButton;
  const isHarmonyTheme = useIsBmHarmonyThemeEnabled();
  return /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      enabledActions: [],
      handlers,
      siteInfo: {
        externalViewer: false
      },
      statusesOverrides: {
        transfer: Statuses.connect
      }
    }
  }, /*#__PURE__*/React.createElement(HarmonyThemeProvider, {
    enabled: isHarmonyTheme
  }, /*#__PURE__*/React.createElement(AccountLevelGateContent, {
    hideBackButton: hideBackButton
  })));
};
//# sourceMappingURL=AccountLevelGate.js.map